/** Shared formatting/download helpers for the statutory registers page (spec §2). */

import { WageSheetColumnKind, WageSheetColumnName } from '../../interfaces/statutoryRegister.interface';

export interface WageSheetColumnMeta {
    key: string;
    label: string;
    kind: WageSheetColumnKind;
}

/**
 * Client-side mirror of WageSheetColumn.java's {key, label, kind} table (decision note
 * 2026-09-11 §3(A)). WageSheetDTO.columns() serialises as plain enum NAME strings (no
 * @JsonFormat(shape=OBJECT) on the enum) -- see the interface file's note -- so this map is what
 * turns "EMPLOYEE_CODE" into a header label and the WageSheetRowDTO field key ("employeeCode")
 * used to look the value up on each row/totals object.
 */
export const WAGE_SHEET_COLUMN_META: Record<WageSheetColumnName, WageSheetColumnMeta> = {
    EMPLOYEE_CODE: { key: 'employeeCode', label: 'Employee Code', kind: 'TEXT' },
    EMPLOYEE_NAME: { key: 'employeeName', label: 'Employee Name', kind: 'TEXT' },
    DESIGNATION: { key: 'designation', label: 'Designation', kind: 'TEXT' },
    DEPARTMENT: { key: 'department', label: 'Department', kind: 'TEXT' },
    DATE_OF_JOINING: { key: 'dateOfJoining', label: 'Date of Joining', kind: 'DATE' },
    WAGE_GRADE: { key: 'wageGrade', label: 'Wage Grade', kind: 'TEXT' },
    WAGE_MODE: { key: 'wageMode', label: 'Wage Mode', kind: 'TEXT' },
    DAYS_IN_MONTH: { key: 'daysInMonth', label: 'Days in Month', kind: 'INT' },
    PAID_DAYS: { key: 'paidDays', label: 'Paid Days', kind: 'INT' },
    ABSENT_DAYS: { key: 'absentDays', label: 'Absent Days', kind: 'INT' },
    UNPAID_LEAVE_DAYS: { key: 'unpaidLeaveDays', label: 'Unpaid Leave Days', kind: 'INT' },
    BASIC: { key: 'basic', label: 'Basic Wage', kind: 'MONEY' },
    HOUSE_RENT: { key: 'houseRent', label: 'House Rent', kind: 'MONEY' },
    MEDICAL: { key: 'medical', label: 'Medical Allowance', kind: 'MONEY' },
    CONVEYANCE: { key: 'conveyance', label: 'Conveyance Allowance', kind: 'MONEY' },
    FOOD: { key: 'food', label: 'Food Allowance', kind: 'MONEY' },
    OTHER_EARNINGS: { key: 'otherEarnings', label: 'Other Earnings', kind: 'MONEY' },
    GROSS_WAGE: { key: 'grossWage', label: 'Gross Wage', kind: 'MONEY' },
    OT_HOURS: { key: 'otHours', label: 'OT Hours', kind: 'HOURS' },
    STATUTORY_OT_RATE: { key: 'statutoryOtRate', label: 'Statutory OT Rate', kind: 'MONEY' },
    EFFECTIVE_OT_RATE: { key: 'effectiveOtRate', label: 'Effective OT Rate', kind: 'MONEY' },
    OT_AMOUNT: { key: 'otAmount', label: 'OT Amount', kind: 'MONEY' },
    OT_RATE_SHORTFALL: { key: 'otRateShortfall', label: 'OT Rate Shortfall', kind: 'FLAG' },
    ATTENDANCE_BONUS: { key: 'attendanceBonus', label: 'Attendance Bonus', kind: 'MONEY' },
    FESTIVAL_BONUS: { key: 'festivalBonus', label: 'Festival Bonus', kind: 'MONEY' },
    PIECE_RATE_AMOUNT: { key: 'pieceRateAmount', label: 'Piece Rate Amount', kind: 'MONEY' },
    LEAVE_ENCASHMENT: { key: 'leaveEncashment', label: 'Leave Encashment', kind: 'MONEY' },
    ABSENCE_DEDUCTION: { key: 'absenceDeduction', label: 'Absence Deduction', kind: 'MONEY' },
    UNPAID_LEAVE_DEDUCTION: { key: 'unpaidLeaveDeduction', label: 'Unpaid Leave Deduction', kind: 'MONEY' },
    OTHER_DEDUCTIONS: { key: 'otherDeductions', label: 'Other Deductions', kind: 'MONEY' },
    TOTAL_DEDUCTIONS: { key: 'totalDeductions', label: 'Total Deductions', kind: 'MONEY' },
    NET_WAGE: { key: 'netWage', label: 'Net Payable', kind: 'MONEY' },
    PAYROLL_STATUS: { key: 'payrollStatus', label: 'Payroll Status', kind: 'TEXT' },
    SIGNATURE: { key: 'signature', label: 'Signature', kind: 'TEXT' },
    // Org-hierarchy dimension columns (FULL preset only; docs/decisions/2026-09-11-garments-org-hierarchy.md §2).
    FLOOR: { key: 'floor', label: 'Floor', kind: 'TEXT' },
    SECTION: { key: 'section', label: 'Section', kind: 'TEXT' },
    LINE: { key: 'line', label: 'Line', kind: 'TEXT' },
};

/** WageSheetFormat's four select options plus FULL, in the same order as the backend enum. */
export const WAGE_SHEET_FORMATS: Array<{ value: 'FULL' | 'BSCI' | 'SEDEX' | 'WRAP' | 'RSC'; label: string }> = [
    { value: 'FULL', label: 'Full (all columns)' },
    { value: 'BSCI', label: 'BSCI' },
    { value: 'SEDEX', label: 'SEDEX (SMETA)' },
    { value: 'WRAP', label: 'WRAP' },
    { value: 'RSC', label: 'RSC' },
];

/** Nullable money renders "—", never 0.00 (spec §1.4 / H4 / M10). */
export const money = (value: number | null | undefined): string =>
    value === null || value === undefined
        ? '—'
        : value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Nullable integer/count fields render "—" the same way (leave carry columns, C2.3). */
export const num = (value: number | null | undefined): string =>
    value === null || value === undefined ? '—' : String(value);

export const dash = (value: string | null | undefined): string => value ?? '—';

// L-3: pinned to 'en-US' (matching money()'s own locale pin) so the dropdown reads the same on
// every runner/workstation regardless of the OS/browser default locale.
export const MONTH_NAMES = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(0, i).toLocaleString('en-US', { month: 'long' }),
}));

/**
 * R19 (T7): whitelist a dynamic filename SEGMENT (an employee code, never a whole filename) to
 * the same {@code [A-Za-z0-9._-]} rule the backend's {@code SafeFilenames.safeFileNameSegment}
 * enforces on the server-side {@code Content-Disposition} header. {@code a.download} on a blob
 * URL is honoured by every browser and OVERRIDES {@code Content-Disposition} entirely — so a
 * segment built from raw server data (an employee code that could contain path separators, CR/LF,
 * or quotes) must be sanitized here too, or the client-side save name silently bypasses the
 * server's own whitelist. Falls back to {@code fallback} when the sanitized segment is blank.
 */
export const sanitizeFilenameSegment = (segment: string | null | undefined, fallback: string): string => {
    if (!segment || !segment.trim()) {
        return fallback;
    }
    const sanitized = segment.replace(/[^A-Za-z0-9._-]/g, '_').slice(0, 40);
    return sanitized.trim() ? sanitized : fallback;
};

/** Blob-download pattern used across the app's XLSX/PDF export buttons (e.g. TaxReports). */
export const triggerDownload = (blob: Blob, filename: string): void => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `(2026, 9)` -> `Sep 2026`. Falls back to the bare month number when out of range. */
export const monthYearLabel = (year: number | null | undefined, month: number | null | undefined): string => {
    if (!month || month < 1 || month > 12) return month == null ? '—' : String(month);
    return year ? `${SHORT_MONTHS[month - 1]} ${year}` : SHORT_MONTHS[month - 1];
};

/**
 * Plain-English versions of the register notes the server sends. Anything not listed passes
 * through unchanged, so a new server note still shows (just unpolished).
 */
const FRIENDLY_NOTES: Array<[RegExp, string]> = [
    [/^FINALIZED means the run was closed/i, 'Finalized means the payroll run has been closed; it does not confirm the money was paid out.'],
    [/^includes legacy rows/i, 'Older payroll rows that the payroll screen may hide are included here.'],
    [/^hours . rate does not reproduce amount/i, 'Hours multiplied by the rate may differ from the amount by a few paisa because each figure is rounded separately.'],
    [/^allocated is total credited/i, 'Allocated is the total credited for the year, including any leave carried forward.'],
    [/^carried-in\/accrued-in-year/i, 'Carried-in and accrued-in-year are shown only for leave that builds up over time. Fixed-entitlement leave shows a dash in those columns.'],
    [/^tenure gate column/i, 'The service-period check applies only to leave that builds up over time; for other leave it shows Yes.'],
];

export const friendlyNote = (note: string): string => {
    const hit = FRIENDLY_NOTES.find(([re]) => re.test(note));
    return hit ? hit[1] : note;
};
